// src/hooks/useKeyboardAndTouchEvents.ts

import { useEffect, useCallback } from 'react';
import { useUIContext } from '@/context/UIContext';
import useSounds from '@/hooks/useSounds';
import styles from '@/components/center/Center.module.scss';

const useKeyboardAndTouchEvents = () => {
  // Get power state and toggle function from UI context; get sound playing function
  const { isPowerOn, togglePowerButton } = useUIContext();
  const { playSound } = useSounds();

  // Play sound based on event type
  const playAppropriateSound = useCallback(
    (type: string) => {
      if (type === 'space' || type === 'touch') {
        playSound('buttonPress');
      }
    },
    [playSound],
  );

  // Handle keyboard events (Space key)
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === 'Space' && !isPowerOn) {
        event.preventDefault();
        togglePowerButton();
        playAppropriateSound('space');
      }
    },
    [isPowerOn, togglePowerButton, playAppropriateSound],
  );

  // Handle touch events on the designated element
  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      if (!isPowerOn) {
        event.preventDefault();
        togglePowerButton();
        playAppropriateSound('touch');
      }
    },
    [isPowerOn, togglePowerButton, playAppropriateSound],
  );

  // Register keyboard event listener
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Register touch event listener on the TV frame element
  useEffect(() => {
    const tvFrame = document.querySelector(`.${styles['center__tv-frame']}`);
    if (tvFrame) {
      tvFrame.addEventListener('touchstart', handleTouchStart as EventListener);
    }
    return () => {
      if (tvFrame) {
        tvFrame.removeEventListener(
          'touchstart',
          handleTouchStart as EventListener,
        );
      }
    };
  }, [handleTouchStart]);

  return null;
};

export default useKeyboardAndTouchEvents;
